import React from 'react';
import { cn } from '../lib/utils';

/**
 * OperatorStat — large-stat card for the admin dashboard.
 *
 * Variants:
 *   tone="brand"  → orange accent (revenue, active customers)
 *   tone="success" → green accent (healthy systems)
 *   tone="warning" → amber accent (pending payments, low balance)
 *   tone="destructive" → red accent (failed payments, errors)
 *   tone="neutral" → no accent (informational)
 *
 * Renders an icon + label, a big value (mono font), an optional delta chip,
 * and a hint line. Use as <OperatorStat /> in Overview, Customers, etc.
 */
export function OperatorStat({
  icon: Icon,
  label,
  value,
  hint,
  delta,            // { value: '+12%', direction: 'up' | 'down' | 'flat' }
  tone = 'neutral',
  loading = false,
  className = '',
}) {
  const toneRing = {
    brand:        'ring-1 ring-brand/20',
    success:      'ring-1 ring-success/20',
    warning:      'ring-1 ring-warning/30',
    destructive:  'ring-1 ring-destructive/30',
    neutral:      '',
  }[tone];

  const toneIcon = {
    brand:       'bg-brand-softest text-brand-strong',
    success:     'bg-success/15 text-success',
    warning:     'bg-warning/15 text-warning',
    destructive: 'bg-destructive/15 text-destructive',
    neutral:     'bg-surface-2 text-fg-muted',
  }[tone];

  return (
    <div className={cn(
      'rounded-xl border border-border bg-surface p-5 transition-shadow hover:shadow-sm',
      toneRing,
      className
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
          {label}
        </div>
        {Icon && (
          <div className={cn(
            'h-7 w-7 rounded-lg flex items-center justify-center shrink-0',
            toneIcon
          )}>
            <Icon className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
      <div className="mt-3">
        {loading ? (
          <div className="h-9 w-24 bg-surface-2 rounded animate-pulse" />
        ) : (
          <div className="text-2xl font-semibold text-fg tabular-nums tracking-tight">
            {value ?? '—'}
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 min-h-[1.25rem]">
        <div className="text-2xs text-fg-muted truncate flex-1">{hint}</div>
        {delta && !loading && (
          <DeltaChip value={delta.value} direction={delta.direction} />
        )}
      </div>
    </div>
  );
}

function DeltaChip({ value, direction = 'flat' }) {
  const palette = {
    up:   'bg-success/15 text-success',
    down: 'bg-destructive/15 text-destructive',
    flat: 'bg-surface-2 text-fg-muted',
  }[direction] || 'bg-surface-2 text-fg-muted';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '·';
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-2xs font-medium tabular-nums',
      palette
    )}>
      <span>{arrow}</span>
      <span>{value}</span>
    </span>
  );
}
